import React, { useEffect, useMemo } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Layers, FolderTree, Shield, Users, LogOut, ShieldCheck } from 'lucide-react';
import { useAdminAuthStore } from '../../store/adminAuthStore';
import { ADMIN_NAV, ADMIN_PERMISSIONS, canAccessHierarchyBuilder } from '../../config/permissions.config';

const ICONS = {
  '/admin/templates': Layers,
  '/admin/hierarchy': FolderTree,
  '/admin/roles': Shield,
  '/admin/users': Users,
} as const;

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const { admin, logout, has } = useAdminAuthStore();

  const navItems = useMemo(() => {
    const perms = admin?.permissions ?? [];
    return ADMIN_NAV.filter((item) => {
      if (item.permission === ADMIN_PERMISSIONS.nodes) {
        return canAccessHierarchyBuilder(perms, has);
      }
      return has(item.permission);
    });
  }, [admin?.permissions, admin?.is_super_admin, has]);

  useEffect(() => {
    document.title = 'RRMS Admin Console';
  }, []);

  const initials = (admin?.name || admin?.username || 'A')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--color-gray-100)]">
      <aside className="w-64 shrink-0 h-screen bg-[var(--color-primary-dark)] text-white flex flex-col">
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
          <ShieldCheck size={22} className="text-[var(--color-accent-light)]" />
          <div>
            <div className="font-semibold leading-tight">Admin Console</div>
            <div className="text-[11px] text-white/50">Dynamic hierarchy &amp; RBAC</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navItems.map((n) => {
            const Icon = ICONS[n.to];
            return (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-sm transition-colors ${
                    isActive ? 'bg-white/15 font-semibold' : 'text-white/75 hover:bg-white/10'
                  }`
                }
              >
                <Icon size={18} />
                {n.label}
              </NavLink>
            );
          })}
          {navItems.length === 0 && (
            <p className="px-3 py-2 text-xs text-white/50">No modules assigned to your role.</p>
          )}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center text-sm font-semibold">{initials}</div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{admin?.name || admin?.username}</div>
              {admin?.is_super_admin && <div className="text-[11px] text-[var(--color-accent-light)]">Super Admin</div>}
            </div>
          </div>
          <button
            className="w-full flex items-center justify-center gap-2 rounded-[var(--radius-md)] border border-white/20 px-3 py-2 text-sm text-white/85 hover:bg-white/10"
            onClick={() => {
              logout();
              navigate('/admin/login', { replace: true });
            }}
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
